import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { vigenteHoy } from '../lib/fecha';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Selector } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import {
  ResumenDeVenta,
  SelectorDeCliente,
  SinTurno,
  conDosDecimales,
  importeVisible,
  type ClienteElegido,
} from '../venta/PiezasDeVenta';
import {
  calcularLineaSegura,
  useVenta,
  type LineaDeVenta,
  type MedioDePago,
  type VarianteEncontrada,
} from '../venta/useVenta';
import { FormasDePago } from '../venta/FormasDePago';

interface Puesto {
  readonly id: string;
  readonly nombre: string;
}

interface Lista {
  readonly id: string;
  readonly nombre: string;
  readonly esPredeterminada: boolean;
  readonly activa: boolean;
  readonly vigenteDesde: string | null;
  readonly vigenteHasta: string | null;
  /** Sus tramos por cantidad (E36): la cuenta de la pantalla los aplica como el servidor. */
  readonly escalas: readonly { desdeCantidad: string; porcentaje: string }[];
}

interface TipoDeComprobante {
  readonly id: string;
  readonly nombre: string;
  readonly activo: boolean;
  readonly tocaCaja: boolean;
  readonly afectaCuenta: boolean;
  readonly familiaFiscal: string | null;
  readonly listaDePreciosId: string | null;
  readonly listaEsFija: boolean;
  readonly requiereOrigen: boolean;
  readonly puedeUsar: boolean;
  /** Si el tipo la fija, quien vende no la elige. */
  readonly puestoDeCajaId: string | null;
}

const MEDIOS: readonly MedioDePago[] = ['efectivo', 'debito', 'credito', 'transferencia'];

/**
 * Carga mayorista: la misma venta, sin soltar el teclado.
 *
 * Un pedido mayorista son veinte o cuarenta líneas cargadas por alguien que sabe los códigos de
 * memoria. Todo lo que en el mostrador ayuda —botones grandes, resultados para tocar— acá
 * estorba: cada vez que hay que ir al mouse se pierden segundos que, multiplicados por
 * cuarenta líneas y por cada pedido del día, son horas.
 *
 * Por eso la carga es una sola fila —código, cantidad, bonificación— y **Enter agrega y
 * devuelve el foco al código**. La bonificación se escribe como la factura el mayorista: "30 y
 * 10" son dos escalones en cascada, no un cuarenta por ciento.
 */
export function Mayorista(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const [puestoId, setPuestoId] = useState('');
  const [listaElegida, setListaElegida] = useState('');
  const [codigo, setCodigo] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [bonificacion, setBonificacion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ultima, setUltima] = useState<{
    numero: string;
    clase: string;
    total: string;
    tokenPublico: string;
  } | null>(null);

  const campoDeCodigo = useRef<HTMLInputElement>(null);

  const puestos = useQuery({
    queryKey: ['puestos-de-caja'],
    queryFn: () => pedir<Puesto[]>('/puestos-de-caja'),
  });
  const puntos = useQuery({
    queryKey: ['puntos-de-venta'],
    queryFn: () => pedir<{ id: string }[]>('/puntos-de-venta'),
  });
  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    queryFn: () => pedir<Lista[]>('/listas-de-precios'),
  });

  /**
   * Con qué se emite.
   *
   * **Faltaba desde E17 y no era otra lógica: era un olvido.** El selector se agregó en el
   * mostrador y esta pantalla quedó emitiendo siempre con el tipo predeterminado, que para un
   * mayorista es justo lo que no sirve: acá es donde se despacha con remito y se factura después.
   */
  const tiposDisponibles = useQuery({
    queryKey: ['tipos-de-comprobante'],
    queryFn: () => pedir<TipoDeComprobante[]>('/tipos-de-comprobante'),
  });
  const encendidos = (tiposDisponibles.data ?? []).filter(
    (tipo) =>
      tipo.activo &&
      tipo.familiaFiscal !== 'devolucion' &&
      tipo.familiaFiscal !== 'nota_de_credito' &&
      // **Los que exigen documento previo no se ofrecen acá**: se emiten desde Facturar entregas,
      // naciendo de lo que ya salió. Ofrecerlos sería invitar a un error que el servidor rechaza.
      !tipo.requiereOrigen &&
      // Ni los que el rol no tiene habilitados. Esconder no es la defensa —el servidor rechaza
      // igual— es cortesía: un menú con opciones que dan error enseña a desconfiar de la pantalla.
      tipo.puedeUsar,
  );
  const [tipoElegido, setTipoElegido] = useState<string>('');
  const tipoActual =
    encendidos.find((tipo) => tipo.id === tipoElegido) ??
    encendidos.find((tipo) => tipo.familiaFiscal === 'ticket_interno') ??
    encendidos[0];
  const cobra = tipoActual === undefined || tipoActual.tocaCaja || tipoActual.afectaCuenta;

  /**
   * En qué caja incide esta venta.
   *
   * **El tipo manda si la fija.** Con una caja declarada, quien vende no la elige: el selector
   * queda bloqueado y a la vista —no escondido, porque saber en qué caja está cobrando es parte de
   * lo que la persona necesita ver—. Es la misma forma que la lista de precios fija de E24.
   */
  const puestoDelTipo = tipoActual?.puestoDeCajaId ?? null;
  const puestoActivo =
    puestoDelTipo ?? (puestoId === '' ? (puestos.data?.[0]?.id ?? '') : puestoId);
  const puntoDeVentaId = puntos.data?.[0]?.id ?? '';
  /**
   * Con qué lista se vende.
   *
   * **El tipo manda si trae una fija**: es lo que pidió el dueño del producto —que un vendedor esté
   * forzado a usar una sola lista por comprobante—. Si no la trae, el mayorista elige, que es el
   * punto entero de tener dos.
   */
  const listaDelTipo = tipoActual?.listaDePreciosId ?? null;
  const listaFijada = tipoActual?.listaEsFija === true && listaDelTipo !== null;
  const [cliente, setCliente] = useState<ClienteElegido | null>(null);
  /**
   * Si nadie la eligió, la cadena del servidor (E36): la del cliente, la del tipo, la de la caja o
   * la de la sucursal, la misma que usa la venta. Cuando la cadena sólo llega a la predeterminada
   * del comercio, esta pantalla sigue proponiendo la mayorista: es para lo que existe.
   */
  const listaQueCorresponde = useQuery({
    queryKey: ['lista-que-corresponde', cliente?.id ?? '', tipoActual?.id ?? '', puestoActivo],
    enabled: puestoActivo !== '' && !listaFijada && listaElegida === '',
    queryFn: () =>
      pedir<{ id: string; nombre: string; origen: string }>(
        `/ventas/lista-que-corresponde?puestoDeCajaId=${puestoActivo}` +
          (cliente === null ? '' : `&terceroId=${cliente.id}`) +
          (tipoActual === undefined ? '' : `&tipoId=${tipoActual.id}`),
      ),
  });
  const porLaCadena =
    listaQueCorresponde.data?.origen === 'comercio' ? undefined : listaQueCorresponde.data?.id;
  const listaId = listaFijada
    ? listaDelTipo
    : listaElegida === ''
      ? (porLaCadena ??
        listas.data?.find((lista) => !lista.esPredeterminada && vigenteHoy(lista))?.id ??
        listas.data?.find((lista) => lista.esPredeterminada)?.id)
      : listaElegida;

  const turno = useQuery({
    queryKey: ['turno-abierto', puestoActivo],
    enabled: puestoActivo !== '',
    queryFn: () => pedir<{ id: string } | null>(`/puestos-de-caja/${puestoActivo}/turno-abierto`),
  });

  const escalas = listas.data?.find((lista) => lista.id === listaId)?.escalas ?? [];
  const venta = useVenta(listaId, escalas);

  async function agregarPorCodigo(): Promise<void> {
    const buscado = codigo.trim();
    if (buscado === '') return;

    try {
      const encontradas = await pedir<VarianteEncontrada[]>(
        `/articulos/buscar?texto=${encodeURIComponent(buscado)}${
          listaId === undefined ? '' : `&listaDePreciosId=${listaId}`
        }`,
      );
      const variante = encontradas[0];
      if (variante === undefined) {
        setError(t('venta.sinResultados'));
        return;
      }
      // Sin precio en la lista elegida, la línea entraría en cero y el error aparecería recién
      // al cobrar, con el cliente esperando y cuarenta líneas cargadas.
      if (variante.precio === null) {
        setError(t('venta.sinPrecioEnLista', { codigo: variante.codigo }));
        return;
      }

      venta.agregar(variante, cantidad.trim() === '' ? '1' : cantidad.trim());
      if (bonificacion.trim() !== '') {
        // La bonificación se aplica a la línea recién agregada. Se separa por comas porque
        // "30, 10" es como se dicta un descuento en cascada por teléfono.
        setBonificacionPendiente(
          bonificacion
            .split(',')
            .map((parte) => parte.trim())
            .filter((parte) => parte !== ''),
        );
      }

      setCodigo('');
      setCantidad('1');
      setBonificacion('');
      setError(null);
    } catch (fallo: unknown) {
      setError(textoDeError(fallo));
    } finally {
      campoDeCodigo.current?.focus();
    }
  }

  // La bonificación se aplica cuando la línea ya está en la lista: agregar y modificar son dos
  // actualizaciones de estado y la segunda no puede leer el resultado de la primera.
  const [bonificacionPendiente, setBonificacionPendiente] = useState<string[] | null>(null);
  useEffect(() => {
    if (bonificacionPendiente === null) return;
    const ultimaLinea = venta.lineas.at(-1);
    if (ultimaLinea !== undefined) {
      venta.cambiar(ultimaLinea.clave, { descuentos: bonificacionPendiente });
    }
    setBonificacionPendiente(null);
  }, [bonificacionPendiente, venta]);

  async function cobrar(): Promise<void> {
    if (venta.lineas.length === 0 || puntoDeVentaId === '' || puestoActivo === '') return;
    try {
      const resultado = await venta.emitir({
        puntoDeVentaId,
        puestoDeCajaId: puestoActivo,
        terceroId: cliente?.id,
        ...(tipoElegido === '' ? {} : { tipoId: tipoElegido }),
        // Un remito o un presupuesto se emiten sin un peso encima: mandarles cobros sería decirle
        // al servidor que se cobró algo que no se cobró.
        cobra,
      });
      setUltima({
        numero: resultado.numero,
        clase: resultado.clase,
        total: resultado.total,
        tokenPublico: resultado.tokenPublico,
      });
      setError(null);
      venta.limpiar();
      setCliente(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
      campoDeCodigo.current?.focus();
    } catch (fallo: unknown) {
      setError(textoDeError(fallo));
    }
  }

  useEffect(() => {
    function alPresionar(evento: KeyboardEvent): void {
      if (evento.key === 'F2') {
        evento.preventDefault();
        void cobrar();
      }
    }
    window.addEventListener('keydown', alPresionar);
    return () => {
      window.removeEventListener('keydown', alPresionar);
    };
  });

  useEffect(() => {
    campoDeCodigo.current?.focus();
  }, []);

  const columnas: Columna<LineaDeVenta>[] = [
    {
      clave: 'codigo',
      titulo: t('venta.codigo'),
      contenido: (linea) => <span className="numero">{linea.codigo}</span>,
    },
    { clave: 'nombre', titulo: t('venta.articulo'), contenido: (linea) => linea.nombre },
    {
      clave: 'cantidad',
      titulo: t('venta.cantidad'),
      numerica: true,
      contenido: (linea) => (
        <input
          aria-label={`${t('venta.cantidad')} ${linea.codigo}`}
          className="numero w-20 rounded-marco border border-borde bg-superficie px-2 py-1 text-right text-sm"
          value={linea.cantidad}
          inputMode="decimal"
          onChange={(evento) => {
            venta.cambiar(linea.clave, { cantidad: evento.target.value });
          }}
        />
      ),
    },
    {
      clave: 'precio',
      titulo: t('venta.precio'),
      numerica: true,
      contenido: (linea) => <span className="numero">{conDosDecimales(linea.precioUnitario)}</span>,
    },
    {
      clave: 'bonificacion',
      titulo: t('venta.bonificacion'),
      numerica: true,
      contenido: (linea) => <span className="numero">{linea.descuentos.join(' + ')}</span>,
    },
    {
      clave: 'importe',
      titulo: t('venta.importe'),
      numerica: true,
      contenido: (linea) => (
        <span className="numero">
          {importeVisible(calcularLineaSegura(linea, escalas).importe)}
        </span>
      ),
    },
    {
      clave: 'quitar',
      titulo: t('venta.quitar'),
      contenido: (linea) => (
        <Boton
          tono="sutil"
          onClick={() => {
            venta.quitar(linea.clave);
          }}
        >
          {t('venta.quitar')}
        </Boton>
      ),
    },
  ];

  if (turno.isSuccess && turno.data === null) {
    return (
      <>
        <Encabezado titulo={t('venta.mayorista')} descripcion={t('venta.descripcionMayorista')} />
        <SinTurno />
      </>
    );
  }

  return (
    <>
      <Encabezado
        titulo={t('venta.mayorista')}
        descripcion={t('venta.descripcionMayorista')}
        acciones={
          <div className="flex gap-2">
            {encendidos.length <= 1 ? null : (
              <div className="w-44">
                <Selector
                  etiqueta={t('venta.queSeEmite')}
                  value={tipoActual?.id ?? ''}
                  onChange={(evento) => {
                    setTipoElegido(evento.target.value);
                  }}
                >
                  {encendidos.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </option>
                  ))}
                </Selector>
              </div>
            )}
            <div className="w-48">
              <Selector
                etiqueta={t('venta.lista')}
                value={listaId ?? ''}
                // Bloqueado y no escondido: quien vende tiene que ver **con qué lista está
                // vendiendo**, aunque no pueda cambiarla. Esconderlo dejaría el precio sin
                // explicación.
                disabled={listaFijada}
                ayuda={listaFijada ? t('venta.listaFijadaPorElTipo') : undefined}
                onChange={(evento) => {
                  setListaElegida(evento.target.value);
                }}
              >
                {(listas.data ?? []).filter(vigenteHoy).map((lista) => (
                  <option key={lista.id} value={lista.id}>
                    {lista.nombre}
                  </option>
                ))}
              </Selector>
            </div>
            <div className="w-44">
              <Selector
                etiqueta={t('venta.puesto')}
                value={puestoActivo}
                disabled={puestoDelTipo !== null}
                ayuda={puestoDelTipo === null ? undefined : t('venta.cajaFijadaPorElTipo')}
                onChange={(evento) => {
                  setPuestoId(evento.target.value);
                }}
              >
                {(puestos.data ?? []).map((puesto) => (
                  <option key={puesto.id} value={puesto.id}>
                    {puesto.nombre}
                  </option>
                ))}
              </Selector>
            </div>
          </div>
        }
      />

      <div className="flex flex-col gap-4">
        <Tarjeta>
          {/* En la carga mayorista el cliente casi siempre está: es a quien se le arma el pedido.
              Va arriba de todo por eso, y sigue sin ser obligatorio. */}
          <div className="mb-3 max-w-sm">
            <SelectorDeCliente cliente={cliente} onElegir={setCliente} />
          </div>
          {/* Una sola fila de carga. Enter agrega y vuelve al código: el ciclo completo sin
              soltar el teclado ni una vez. */}
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(evento) => {
              evento.preventDefault();
              void agregarPorCodigo();
            }}
          >
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-texto-tenue" htmlFor="codigo-mayorista">
                {t('venta.codigo')}
              </label>
              <input
                id="codigo-mayorista"
                ref={campoDeCodigo}
                value={codigo}
                autoComplete="off"
                className="w-56 rounded-marco border border-borde bg-superficie px-3 py-2 text-base"
                onChange={(evento) => {
                  setCodigo(evento.target.value);
                }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-texto-tenue" htmlFor="cantidad-mayorista">
                {t('venta.cantidad')}
              </label>
              <input
                id="cantidad-mayorista"
                value={cantidad}
                inputMode="decimal"
                className="numero w-24 rounded-marco border border-borde bg-superficie px-3 py-2 text-right text-base"
                onChange={(evento) => {
                  setCantidad(evento.target.value);
                }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-texto-tenue" htmlFor="bonif-mayorista">
                {t('venta.bonificacion')}
              </label>
              <input
                id="bonif-mayorista"
                value={bonificacion}
                inputMode="decimal"
                placeholder="30, 10"
                className="numero w-28 rounded-marco border border-borde bg-superficie px-3 py-2 text-right text-base"
                onChange={(evento) => {
                  setBonificacion(evento.target.value);
                }}
              />
            </div>

            <Boton tono="principal" type="submit">
              {t('venta.agregar')}
            </Boton>
          </form>

          <p className="mt-2 text-xs text-texto-tenue">{t('venta.atajosMayorista')}</p>
        </Tarjeta>

        <Tarjeta>
          <Tabla
            columnas={columnas}
            filas={venta.lineas}
            claveDeFila={(linea) => linea.clave}
            vacio={<p className="py-8 text-center text-sm text-texto-tenue">{t('venta.vacia')}</p>}
          />
        </Tarjeta>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_22rem]">
          <Tarjeta>
            <FormasDePago
              medios={MEDIOS}
              pagos={venta.pagos}
              faltante={venta.faltante}
              onElegirUnico={venta.setMedioDePago}
              onAgregar={venta.agregarPago}
              onCambiar={venta.cambiarPago}
              onQuitar={venta.quitarPago}
            />

            {error === null ? null : (
              <p role="alert" className="mt-3 text-xs text-mal">
                {error}
              </p>
            )}
            {ultima === null ? null : (
              <p role="status" className="mt-3 text-sm text-bien">
                {t('venta.cobrada', {
                  clase: ultima.clase,
                  numero: ultima.numero,
                  total: conDosDecimales(ultima.total),
                })}{' '}
                <a
                  className="text-acento underline"
                  href={`/t/${ultima.tokenPublico}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('ticket.verTicket')}
                </a>
              </p>
            )}
          </Tarjeta>

          <Tarjeta>
            <ResumenDeVenta totales={venta.totales} />
            <div className="mt-4 flex flex-col gap-2">
              <Boton
                tono="principal"
                cargando={venta.emitiendo}
                // El servidor exige que la suma dé el total exacto; que el botón se apague evita
                // descubrirlo recién al apretarlo.
                disabled={venta.lineas.length === 0 || (cobra && !venta.faltante.esCero())}
                onClick={() => {
                  void cobrar();
                }}
              >
                {t('venta.cobrar')}
              </Boton>
              <Boton
                tono="sutil"
                disabled={venta.lineas.length === 0}
                onClick={() => {
                  venta.limpiar();
                  campoDeCodigo.current?.focus();
                }}
              >
                {t('venta.cancelar')}
              </Boton>
            </div>
          </Tarjeta>
        </div>
      </div>
    </>
  );
}
